import { useMemo } from 'react';
import { useApolloClient, useQuery } from 'react-apollo';
import { useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import condominiumContextGql from '../utils/condominium-context.gql';
import useMe from './useMe';

const condominiumGql = gql`
  query condominium($condominiumId: ID) {
    condominium(condominiumId: $condominiumId) {
      _id
      name
      referral
    }
  }
`;

export default function useContext() {
  const [me] = useMe();
  const params = useParams();
  const apolloClient = useApolloClient();

  const write = async ({ condominiumId, callback }) => {
    await apolloClient.writeQuery({
      query: condominiumContextGql,
      data: {
        condominiumContext: {
          condominium: condominiumId,
          __typename: 'CondominiumData',
        },
      },
    });
    if (callback) callback();
  };

  const { data, loading } = useQuery(condominiumContextGql, {
    skip: !me,
    onCompleted: args => {
      if (args) return;

      let condominiumId;

      if (params.condominiumId) {
        condominiumId = params.condominiumId;
      } else if (me.condominiums && me.condominiums.length) {
        condominiumId = me.condominiums[0].data;
      }

      if (condominiumId) {
        write({ condominiumId });
      }
    },
  });

  const condominiumId = useMemo(() => {
    if (!data || !data.condominiumContext) return null;
    return data.condominiumContext.condominium;
  }, [data]);

  const { data: condominiumData } = useQuery(condominiumGql, {
    skip: !me || !condominiumId,
    variables: {
      condominiumId,
    },
  });

  const condominium = useMemo(() => {
    if (!condominiumData || !condominiumData.condominium) return null;
    return condominiumData.condominium;
  }, [condominiumData]);

  const { isJanitor, approved, permission, role, status } = useMemo(() => {
    const defaultValues = {
      isJanitor: false,
      approved: false,
      permission: false,
      role: '',
    };
    if (!me || !condominium) return defaultValues;
    const entry = me.condominiums.find(x => x.data === condominium._id);
    if (!entry) return defaultValues;

    let permissionType;
    if (entry.permissions.vote) permissionType = 'vote';
    else if (entry.permissions.comment) permissionType = 'comment';
    else permissionType = 'view';

    return {
      permission: permissionType,
      isJanitor: entry.role === 'janitor',
      approved: entry.status === 'approved',
      role: entry.role,
      status: entry.status,
    };
  }, [me, condominium]);

  return {
    isLoading: !condominium || loading,
    condominium,
    condominiumId,
    write,
    isJanitor,
    approved,
    permission,
    role,
    status,
  };
}
